import React, { useState } from 'react';
import { getProductAssessment, FREQUENCY_OPTIONS } from '../scoring/edcScoring';
import { ShieldAlert, ShieldCheck, Shield, HelpCircle, Activity, Info, Plus, Trash2, Sparkles } from 'lucide-react';
import FactorDetailModal from './FactorDetailModal';

export default function ProductCard({ product, onAdd, onRemove, showDetails, expanded, initialFrequency = 'daily', avoidSignal }) {
    const ConfidenceDot = ({ level }) => {
        if (level === 'high') {
            return <svg width="10" height="10" viewBox="0 0 10 10" style={{ marginRight: 6, opacity: 0.8, flexShrink: 0 }}><circle cx="5" cy="5" r="5" fill="currentColor"/></svg>;
        } else if (level === 'moderate') {
            return <svg width="10" height="10" viewBox="0 0 10 10" style={{ marginRight: 6, opacity: 0.8, flexShrink: 0 }}><circle cx="5" cy="5" r="4.5" fill="none" stroke="currentColor"/><path d="M5,0.5 A4.5,4.5 0 0,0 5,9.5 Z" fill="currentColor"/></svg>;
        } else {
            return <svg width="10" height="10" viewBox="0 0 10 10" style={{ marginRight: 6, opacity: 0.6, flexShrink: 0 }}><circle cx="5" cy="5" r="4.5" fill="none" stroke="currentColor"/></svg>;
        }
    };
    const [selectedFreq, setSelectedFreq] = useState(initialFrequency);
    const [selectedSignal, setSelectedSignal] = useState(null);
    const [selectedSignalType, setSelectedSignalType] = useState(null);

    const handleSignalClick = (signal, type) => (e) => {
        e.stopPropagation();
        setSelectedSignal(signal);
        setSelectedSignalType(type);
    };

    const scoringProduct = {
        ...product,
        ingredients: product.ingredients || product.ingredients_text || '',
    };

    // We pass the local state frequency so it updates live within the card
    const assessment = getProductAssessment(scoringProduct, selectedFreq);
    const { signalLevel, confidence, chemicalSignals, foodSignals, topDrivers, usageContext } = assessment;

    // Badge styling based on signal
    let badgeClass = 'signal-unclear';
    let BadgeIcon = HelpCircle;
    if (signalLevel === 'Low Signal') {
        badgeClass = 'signal-low';
        BadgeIcon = ShieldCheck;
    } else if (signalLevel === 'Moderate Signal') {
        badgeClass = 'signal-moderate';
        BadgeIcon = Shield;
    } else if (signalLevel === 'Elevated Signal') {
        badgeClass = 'signal-elevated';
        BadgeIcon = ShieldAlert;
    }

    const categoryLabels = {
        'personal-care': 'Personal Care',
        'food': 'Food',
        'household': 'Household Cleaning',
        'baby-care': 'Baby Care',
        'food-packaging': 'Packaging',
    };

    const hasAvoidedSignal = avoidSignal
        ? [...chemicalSignals].some(s => s.shortReason.toLowerCase().includes(avoidSignal.toLowerCase())) ||
          [...foodSignals].some(s => s.reason.toLowerCase().includes(avoidSignal.toLowerCase()))
        : false;

    const isSaferSwap = avoidSignal && !hasAvoidedSignal && signalLevel !== 'Unclear';

    const handleAddClick = (e) => {
        e.stopPropagation();
        onAdd({ ...product, frequency: selectedFreq });
    };

    const handleRemoveClick = (e) => {
        e.stopPropagation();
        if (onRemove) onRemove();
    };

    return (
        <div className={`product-card ${expanded ? 'expanded' : ''}`} onClick={showDetails}>

            {/* --- Compact View --- */}
            <div className="product-header">
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className="product-name">{product.name || product.product_name || 'Unknown Item'}</div>
                    <div className="product-brand">{product.brand || product.brands || ''}</div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                    {isSaferSwap && (
                        <div className="signal-badge" style={{ backgroundColor: '#10b981', color: '#fff', fontSize: 11, padding: '2px 6px' }}>
                            <Sparkles size={12} style={{ marginRight: 4 }} /> Safer Swap
                        </div>
                    )}
                    <div className={`signal-badge ${badgeClass}`}>
                        <BadgeIcon size={14} />
                        <span>{signalLevel}</span>
                    </div>
                </div>
            </div>

            <div className="product-meta-row">
                <span className="product-category">
                    {categoryLabels[product.category] || product.category || 'Item'}
                </span>
            </div>
            <div className="product-meta-row" style={{ marginTop: 2 }}>
                <span className={`confidence-text conf-${confidence}`} style={{ fontSize: 11 }}>
                    Confidence: {confidence}
                </span>
            </div>

            {!expanded && topDrivers.length > 0 && (
                <div className="top-drivers">
                    <span className="drivers-label">Drivers:</span> {topDrivers.join(', ')}
                </div>
            )}

            {!expanded && onRemove && (
                <button className="remove-btn-compact" onClick={handleRemoveClick}>
                    <Trash2 size={14} /> Remove
                </button>
            )}

            {/* --- Expanded Detail View --- */}
            {expanded && (
                <div className="product-details-drawer">

                    {/* Why flagged */}
                    <div className="detail-section">
                        <div className="detail-heading">
                            <Activity size={14} />
                            <span>Why it was flagged</span>
                        </div>

                        {chemicalSignals.length === 0 && foodSignals.length === 0 && signalLevel !== 'Unclear' && (
                            <div className="detail-empty">No tracked signals found in ingredient data.</div>
                        )}

                        {signalLevel === 'Unclear' && (
                            <div className="detail-empty">Not enough ingredient or food data to assess this item reliably.</div>
                        )}

                        {chemicalSignals.length > 0 && (
                            <ul className="signal-list">
                                {chemicalSignals.map((sig, i) => (
                                    <li key={i} className="signal-item clickable" onClick={handleSignalClick(sig, 'chemical')}>
                                        <div className="signal-item-header">
                                            <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text-primary)' }}>
                                                <ConfidenceDot level={sig.confidence} />
                                                <strong>{sig.shortReason}</strong>
                                            </div>
                                            <span className={`evidence-badge ${sig.evidenceType || 'proxy'}`}>{sig.evidenceType || 'proxy'}</span>
                                        </div>
                                        <div className="signal-item-desc">{sig.detailReason}</div>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {foodSignals.length > 0 && (
                            <div style={{ marginTop: 8 }}>
                                <div className="sub-heading">Dietary Factors</div>
                                <ul className="signal-list">
                                    {foodSignals.map((fs, i) => (
                                        <li key={`fs-${i}`} className="signal-item clickable" onClick={handleSignalClick(fs, 'food')}>
                                            <div className="signal-item-header">
                                                <div style={{ display: 'flex', alignItems: 'center', color: 'var(--text-primary)' }}>
                                                    <ConfidenceDot level={fs.confidence || 'moderate'} />
                                                    <strong>{fs.reason}</strong>
                                                </div>
                                                <span className="evidence-badge food-tag">FOOD DATA</span>
                                            </div>
                                            <div className="signal-item-desc">{fs.mechanism}</div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>

                    {/* Context & Actions */}
                    <div className="detail-section">
                        <div className="detail-heading">
                            <Info size={14} />
                            <span>Use Context</span>
                        </div>

                        <div className="context-row">
                            <span className="context-label">Route:</span>
                            <span className="context-value">{usageContext}</span>
                        </div>

                        {onAdd && (
                            <div className="context-row" onClick={(e) => e.stopPropagation()}>
                                <span className="context-label">Frequency:</span>
                                <select
                                    className="frequency-select"
                                    value={selectedFreq}
                                    onChange={(e) => setSelectedFreq(e.target.value)}
                                >
                                    {FREQUENCY_OPTIONS.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </select>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="detail-actions" onClick={(e) => e.stopPropagation()}>
                        {onAdd && (
                            <button className="btn-action primary" onClick={handleAddClick}>
                                <Plus size={16} /> Log this item
                            </button>
                        )}
                        {onRemove && (
                            <button className="btn-action destructive" onClick={handleRemoveClick}>
                                <Trash2 size={16} /> Remove from log
                            </button>
                        )}
                    </div>

                    {/* Disclaimer */}
                    <div className="detail-disclaimer">
                        Signals are estimate-based and reflect ingredient, processing, and exposure heuristics. This is not a medical diagnosis.
                    </div>

                </div>
            )}

            {selectedSignal && (
                <FactorDetailModal
                    signal={selectedSignal}
                    signalType={selectedSignalType}
                    productCategory={product.category}
                    onClose={() => { setSelectedSignal(null); setSelectedSignalType(null); }}
                />
            )}
        </div>
    );
}
